import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const PhoneIcon = ({ size = 16, color = '#c9935a' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z" />
  </svg>
);

function Threads() {
  const [threads, setThreads] = useState([]);
  const [activeMenu, setActiveMenu] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const pressTimerRef = useRef(null);
  const { companionId } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchThreads();
  }, []);

  const fetchThreads = () => {
    axios.get(`${API}/threads/${companionId}?token=${token}`)
      .then(res => setThreads(res.data.threads))
      .catch((err) => console.log(err));
  };

  const getTodayTitle = () => {
    return new Date().toLocaleDateString([], { month: 'long', day: 'numeric' });
  };

  const newThread = async () => {
    const currentToken = window.localStorage.getItem('token');
    const res = await axios.post(`${API}/threads`, {
      token: currentToken,
      companion_id: companionId,
      title: getTodayTitle()
    });
    window.location.href = `/chat/${companionId}/${res.data.id}`;
  };

  const formatRelativeTime = (timestamp) => {
    if (!timestamp) return '';
    const ts = timestamp.endsWith('Z') ? timestamp : timestamp + 'Z';
    const date = new Date(ts);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 1000 / 60);
    const diffHours = Math.floor(diffMins / 60);

    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;

    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    if (date.toDateString() === yesterday.toDateString()) return 'yesterday';

    if (date.getFullYear() === now.getFullYear()) {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    }
    return date.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const handlePressStart = (id) => {
    pressTimerRef.current = setTimeout(() => {
      setActiveMenu(id);
    }, 500);
  };

  const handlePressEnd = () => {
    if (pressTimerRef.current) {
      clearTimeout(pressTimerRef.current);
      pressTimerRef.current = null;
    }
  };

  const closeMenu = () => setActiveMenu(null);

  const startRename = (thread) => {
    closeMenu();
    setEditingId(thread.id);
    setEditTitle(thread.title);
  };

  const saveRename = async (id) => {
    try {
      await axios.put(`${API}/threads/${id}/rename`, { token, title: editTitle });
      setThreads(prev => prev.map(t => t.id === id ? { ...t, title: editTitle } : t));
    } catch(err) {
      console.log('rename error:', err);
    }
    setEditingId(null);
    setEditTitle('');
  };

  const deleteThread = async (id) => {
    closeMenu();
    try {
      await axios.delete(`${API}/threads/${id}?token=${token}`);
      setThreads(prev => prev.filter(t => t.id !== id));
    } catch(err) {
      console.log('delete error:', err);
    }
  };

  return (
    <div style={styles.container}>
      {activeMenu && <div style={styles.overlay} onClick={closeMenu} />}

      <div style={styles.header}>
        <button style={styles.backBtn} onClick={() => navigate('/companions/list')}>←</button>
        <div style={styles.title}>Conversations</div>
        <div style={styles.headerRight}>
          <button style={styles.callBtn} onClick={() => navigate(`/call/${companionId}`)} title="calls">
            <PhoneIcon size={15} color="#c9935a" />
          </button>
          <button style={styles.newBtn} onClick={newThread}>+</button>
        </div>
      </div>

      <div style={styles.list}>
        {threads.length === 0 && (
          <div style={styles.empty}>no conversations yet 🥀<br/>tap + to start one</div>
        )}
        {threads.map(t => (
          <div key={t.id} style={styles.cardWrap}>
            {activeMenu === t.id && (
              <div style={styles.contextMenu}>
                <button style={styles.menuBtn} onClick={() => startRename(t)}>✏️ rename</button>
                <div style={styles.menuDivider} />
                <button style={{...styles.menuBtn, ...styles.menuBtnDanger}} onClick={() => deleteThread(t.id)}>🗑 delete</button>
              </div>
            )}
            {editingId === t.id ? (
              <div style={styles.card}>
                <input
                  style={styles.renameInput}
                  value={editTitle}
                  onChange={e => setEditTitle(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && saveRename(t.id)}
                  autoFocus
                />
                <div style={styles.renameActions}>
                  <button style={styles.editCancel} onClick={() => setEditingId(null)}>cancel</button>
                  <button style={styles.editSave} onClick={() => saveRename(t.id)}>save</button>
                </div>
              </div>
            ) : (
              <div
                style={styles.card}
                onClick={() => !activeMenu && navigate(`/chat/${companionId}/${t.id}`)}
                onMouseDown={() => handlePressStart(t.id)}
                onMouseUp={handlePressEnd}
                onMouseLeave={handlePressEnd}
                onTouchStart={() => handlePressStart(t.id)}
                onTouchEnd={handlePressEnd}
              >
                <div style={styles.cardContent}>
                  <div style={styles.cardTitle}>{t.title}</div>
                  <div style={styles.cardPreview}>{t.last_message || 'no messages yet'}</div>
                </div>
                <div style={styles.cardDate}>
                  {formatRelativeTime(t.last_message_at)}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

const styles = {
  container: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    background: '#0e0b0f',
    fontFamily: "'DM Sans', sans-serif",
    display: 'flex', flexDirection: 'column',
    overflowY: 'hidden',
  },
  overlay: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 50,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '20px 24px 16px',
    background: 'linear-gradient(180deg, #120f18 0%, #0e0b0f 100%)',
    borderBottom: '1px solid rgba(201,147,90,0.06)',
    flexShrink: 0,
  },
  backBtn: {
    width: '36px', height: '36px', borderRadius: '50%',
    border: '1px solid rgba(201,147,90,0.15)', background: 'transparent',
    color: '#c9935a', fontSize: '16px', cursor: 'pointer',
  },
  title: {
    fontSize: '18px', color: '#e8ddd0', fontWeight: '300',
    fontStyle: 'italic', fontFamily: "'Cormorant Garamond', serif",
  },
  headerRight: { display: 'flex', alignItems: 'center', gap: '8px' },
  callBtn: {
    width: '36px', height: '36px', borderRadius: '50%',
    border: '1px solid rgba(201,147,90,0.15)', background: 'transparent',
    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  newBtn: {
    width: '36px', height: '36px', borderRadius: '50%', border: 'none',
    background: 'linear-gradient(135deg, #8a5f35, #c9935a)',
    color: '#0e0b0f', fontSize: '20px', cursor: 'pointer',
    boxShadow: '0 4px 12px rgba(201,147,90,0.3)',
  },
  list: {
    flex: 1,
    overflowY: 'auto',
    WebkitOverflowScrolling: 'touch',
    display: 'flex', flexDirection: 'column',
    gap: '10px', padding: '20px 24px 80px',
  },
  empty: {
    color: '#4a3d30', fontSize: '14px', textAlign: 'center',
    marginTop: '48px', lineHeight: '2', fontFamily: "'DM Sans', sans-serif",
  },
  cardWrap: {
    position: 'relative', display: 'flex', flexDirection: 'column',
  },
  contextMenu: {
    display: 'flex', flexDirection: 'row', gap: '4px',
    background: '#1e1825', border: '1px solid rgba(201,147,90,0.15)',
    borderRadius: '14px', padding: '6px',
    boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
    marginBottom: '6px', zIndex: 100, alignSelf: 'flex-end',
  },
  menuBtn: {
    display: 'flex', alignItems: 'center', gap: '6px',
    padding: '8px 14px', borderRadius: '10px', border: 'none',
    background: 'transparent', color: '#e8ddd0',
    fontFamily: "'DM Sans', sans-serif", fontSize: '13px',
    fontWeight: '300', cursor: 'pointer', whiteSpace: 'nowrap',
  },
  menuBtnDanger: { color: '#c9935a' },
  menuDivider: { width: '1px', background: 'rgba(201,147,90,0.1)', margin: '4px 0' },
  card: {
    background: '#1e1825', borderRadius: '16px',
    border: '1px solid rgba(201,147,90,0.08)',
    padding: '16px 18px', display: 'flex',
    alignItems: 'center', justifyContent: 'space-between',
    cursor: 'pointer', gap: '12px',
    WebkitUserSelect: 'none', userSelect: 'none', WebkitTouchCallout: 'none',
  },
  cardContent: { flex: 1, overflow: 'hidden' },
  cardTitle: {
    fontSize: '14px', color: '#e8ddd0', fontWeight: '400',
    marginBottom: '4px', fontFamily: "'DM Sans', sans-serif",
  },
  cardPreview: {
    fontSize: '12px', color: '#4a3d30', fontWeight: '300',
    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
    maxWidth: '85%', fontFamily: "'DM Sans', sans-serif",
  },
  cardDate: {
    fontSize: '11px', color: '#4a3d30',
    flexShrink: 0, fontFamily: "'DM Sans', sans-serif",
  },
  renameInput: {
    flex: 1, background: 'transparent', border: 'none',
    borderBottom: '1px solid rgba(201,147,90,0.3)',
    color: '#e8ddd0', fontFamily: "'DM Sans', sans-serif",
    fontSize: '14px', outline: 'none', padding: '4px 0',
  },
  renameActions: { display: 'flex', gap: '8px', marginLeft: '12px' },
  editCancel: {
    padding: '6px 12px', borderRadius: '10px',
    border: '1px solid rgba(201,147,90,0.15)', background: 'transparent',
    color: '#4a3d30', fontFamily: "'DM Sans', sans-serif", fontSize: '12px', cursor: 'pointer',
  },
  editSave: {
    padding: '6px 12px', borderRadius: '10px', border: 'none',
    background: 'linear-gradient(135deg, #8a5f35, #c9935a)',
    color: '#0e0b0f', fontFamily: "'DM Sans', sans-serif", fontSize: '12px', cursor: 'pointer',
  },
};

export default Threads;
